import { formatNumber } from 'utils/numberFormat';
import { isQuickChat } from 'productLine';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';

const useAideEntry = (
  regionDataMap: Partial<Record<General.UpperCaseRegion, Aide.Overview>>,
  overviewData?: Aide.Overview,
) => {
  const { t } = useTranslate();

  const data = useMemo(() => {
    type ScenarioMap = Partial<Record<Aide.ScenarioName, Aide.Scenario>>;

    if (!overviewData) {
      return {
        hasData: false,
        enabledTaskCount: 0,
        totalTaskCount: 0,
        scenarioMap: {} as ScenarioMap,
      };
    }

    const { scenarios } = overviewData;

    const scenarioMap = scenarios.reduce<ScenarioMap>((res, cur) => {
      const { name, enabledConfigs, enabledStoreCount } = cur;

      if (isQuickChat && name === 'BASIC_RECEPTION') {
        res.phrase = {
          name: 'phrase',
          allConfigs: ['PHRASE'],
          enabledConfigs: enabledConfigs.includes('PHRASE') ? ['PHRASE'] : [],
          enabledStoreCount,
        };

        res.welcome = {
          name: 'welcome',
          allConfigs: ['WELCOME_REPLY'],
          enabledConfigs: enabledConfigs.includes('WELCOME_REPLY') ? ['WELCOME_REPLY'] : [],
          enabledStoreCount,
        };

        res.timeout = {
          name: 'timeout',
          allConfigs: ['TIMEOUT_REPLY'],
          enabledConfigs: enabledConfigs.includes('TIMEOUT_REPLY') ? ['TIMEOUT_REPLY'] : [],
          enabledStoreCount,
        };
      } else {
        res[name] = cur;
      }

      return res;
    }, {});

    return {
      hasData: true,
      ...overviewData,
      scenarioMap,
    };
  }, [overviewData]);

  const carouselList = useMemo(() => {
    const scenarioCarouselMap: Record<Aide.ScenarioName, (x: string | number) => string> = {
      ...(isQuickChat
        ? {}
        : {
            BASIC_RECEPTION: (x: string | number) => t('aide.x 店铺已开启基础接待', { x }),
            AI_RECEPTION: (x: string | number) => t('aide.x 店铺已开启AI智能接待', { x }),
          }),
      ORDER_REMINDER: (x: string | number) => t('aide.x 店铺已开启催下单', { x }),
      PAYMENT_REMINDER: (x: string | number) => t('aide.x 店铺已开启催付款', { x }),
      REVIEW_MANAGEMENT_V2: (x: string | number) => t('aide.x 店铺已开启评价管理', { x }),
      LOGISTICS_CARE: (x: string | number) => t('aide.x 店铺已开启物流呵护', { x }),
      ORDER_ABANDON_REMINDER: (x: string | number) => t('aide.x 店铺已开启弃单挽回', { x }),
      CSAT_INVITE_REVIEW: (x: string | number) => `${x} 店铺已开启智能CSAT邀评`,
    };

    const scenarioCountMap = Object.values(regionDataMap).reduce<
      Partial<Record<Aide.ScenarioName, number>>
    >((res, overview) => {
      const { scenarios } = overview;

      return scenarios.reduce((res, scenario) => {
        const { name, enabledStoreCount } = scenario;

        res[name] = (res[name] || 0) + enabledStoreCount;

        return res;
      }, res);
    }, {});

    return Object.keys(scenarioCountMap).reduce<string[]>((res, key) => {
      const { [key]: count } = scenarioCountMap;

      const { [key]: calculate } = scenarioCarouselMap;

      if (!count || !calculate) {
        return res;
      }

      res.push(calculate(formatNumber(count >= 10000 ? count : count + 10000)));

      return res;
    }, []);
  }, [regionDataMap, t]);

  return {
    ...data,
    carouselList,
  };
};

export default useAideEntry;
